// Мастер создания и редактирования упражнений (3 шага): выбор типа, заголовок/настройки, варианты.
// Используется на странице управления упражнениями для преподавателей.

import { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Row,
  Col,
  Collapse,
  InputGroup,
  Badge,
} from 'react-bootstrap';
import useExerciseStore from '../../store/exerciseStore';
import { PARSERS, FORM_TYPES } from '../../constants/exerciseParsers';
import GrammarRuleModal from './GrammarRuleModal';
import { getComplexityLevels } from '../../api/exerciseApi';

const LEVEL_VARIANTS = ['success', 'info', 'primary', 'warning', 'danger'];

const LEVEL_IDS = [1, 2, 3, 4, 5];

const LEVEL_LABEL = {
  1: 'Beginner',
  2: 'Elementary',
  3: 'Pre-Intermediate',
  4: 'Intermediate',
  5: 'Advanced',
};

// Keyed variant (singular) — для использования по ID
const LEVEL_VARIANT = {
  1: 'success',
  2: 'info',
  3: 'primary',
  4: 'warning',
  5: 'danger',
};

// ── Step Indicator ────────────────────────────────────────────────────────────

function StepIndicator({ step, isEditMode }) {
  if (isEditMode) return null;

  const steps = [
    { num: 1, label: 'Тип' },
    { num: 2, label: 'Заголовок' },
    { num: 3, label: 'Варианты' },
  ];

  return (
    <div className="ewl-step-indicator mb-1">
      {steps.map((s, i) => (
        <>
          <div
            key={s.num}
            className={`ewl-step ${step === s.num ? 'active' : step > s.num ? 'done' : ''}`}
          >
            <div className="ewl-step-dot">
              {step > s.num ? '✓' : s.num}
            </div>
            <span className="d-none d-sm-inline">{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`ewl-step-line ${step > s.num ? 'done' : ''}`} />
          )}
        </>
      ))}
    </div>
  );
}

// ── Variant Row ───────────────────────────────────────────────────────────────

function VariantRow({ variant, onDelete, deleting, onLinkRule }) {
  // Подсчитываем количество предложений из нового поля sentences
  const itemCount = variant.sentences ? Object.keys(variant.sentences).length : 0;
  const hasRule = Boolean(variant.grammar_rule_id);
  // Берём первое предложение для превью
  const firstSentence = variant.sentences
    ? Object.values(variant.sentences)[0]?.sentence ?? ''
    : '';
  return (
    <div
      className="d-flex align-items-center justify-content-between gap-3 p-3"
      style={{
        background: '#f8f9fc',
        border: '1px solid #e9ecef',
        borderRadius: '0.6rem',
        marginBottom: '0.5rem',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fw-medium" style={{ fontSize: '0.875rem', color: '#1e2a4a' }}>
          {variant.variant ? `Вариант ${variant.variant}` : 'Вариант'}
          {hasRule && (
            <Badge bg="info" className="ms-2" style={{ fontSize: '0.65rem', fontWeight: 500 }}>
              правило
            </Badge>
          )}
        </div>
        {itemCount > 0 && (
          <div className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.125rem' }}>
            {itemCount} {itemCount === 1 ? 'предложение' : itemCount < 5 ? 'предложения' : 'предложений'}
          </div>
        )}
        {firstSentence && (
          <div
            className="text-muted mt-1"
            style={{
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              maxHeight: '2.5rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {firstSentence}
          </div>
        )}
      </div>
      <div className="d-flex gap-2 flex-shrink-0">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onLinkRule}
          style={{ borderRadius: '0.4rem', fontSize: '0.75rem' }}
        >
          {hasRule ? 'Редактировать правило' : 'Добавить правило'}
        </Button>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={onDelete}
          disabled={deleting}
          style={{ borderRadius: '0.4rem' }}
        >
          {deleting ? <Spinner size="sm" animation="border" /> : '✕'}
        </Button>
      </div>
    </div>
  );
}

// ── Main Wizard ───────────────────────────────────────────────────────────────

export default function ExerciseWizard({ show, onHide, exercise }) {
  const {
    types,
    prompts,
    createExerciseType,
    fetchPrompts,
    createPrompt,
    deletePrompt,
    parseText,
    createExercise,
    updateExercise,
    fetchVariants,
    createVariant,
    deleteVariant,
  } = useExerciseStore();

  const isEditMode = Boolean(exercise);

  // ── Step ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Step 1 state ──────────────────────────────────────────────────────────
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [showNewTypeForm, setShowNewTypeForm] = useState(false);
  const [newType, setNewType] = useState({
    name: '',
    description: '',
    hard_level: 1,
    parser: 'keyword_transform',
    form_type: 'keyword_transform',
  });
  const [typeError, setTypeError] = useState('');
  const [creatingType, setCreatingType] = useState(false);

  // ── Step 2 state ──────────────────────────────────────────────────────────
  const [exerciseName, setExerciseName] = useState('');
  const [task, setTask] = useState('');
  const [description, setDescription] = useState('');
  const [complexityLevelId, setComplexityLevelId] = useState(2);
  const [complexityLevels, setComplexityLevels] = useState([]);
  const [headerSaving, setHeaderSaving] = useState(false);
  const [headerError, setHeaderError] = useState('');

  // ID созданного/редактируемого упражнения — нужен для привязки вариантов
  const [currentExerciseId, setCurrentExerciseId] = useState(null);

  // ── Step 3 state ──────────────────────────────────────────────────────────
  const [variants, setVariants] = useState([]);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [deletingVariantId, setDeletingVariantId] = useState(null);

  // Prompt state
  const [promptMode, setPromptMode] = useState('saved');
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [prompt, setPrompt] = useState('');
  const [promptOpen, setPromptOpen] = useState(false);
  const [savePromptName, setSavePromptName] = useState('');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [savePromptError, setSavePromptError] = useState('');
  const [deletingPromptId, setDeletingPromptId] = useState(null);

  // Parse state
  const [rawText, setRawText] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [parsedBlock, setParsedBlock] = useState(null);
  const [editedAnswers, setEditedAnswers] = useState({});
  const [savingVariant, setSavingVariant] = useState(false);
  const [variantSaveError, setVariantSaveError] = useState('');

  // Grammar rule state
  const [lastSavedVariantId, setLastSavedVariantId] = useState(null);
  const [lastSavedKeyword, setLastSavedKeyword] = useState('');
  const [lastSavedQuestion, setLastSavedQuestion] = useState('');
  const [showGrammarRulePrompt, setShowGrammarRulePrompt] = useState(false);
  const [showGrammarRuleModal, setShowGrammarRuleModal] = useState(false);
  const [grammarRuleVariant, setGrammarRuleVariant] = useState(null); // вариант для редактирования правила

  // ── Init ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!show) return;

    fetchPrompts().then((list) => {
      if (list.length > 0) {
        setSelectedPromptId(String(list[0].id));
        setPrompt(list[0].body);
        setPromptMode('saved');
      } else {
        setPromptMode('custom');
      }
    });

    getComplexityLevels().then((res) => {
      setComplexityLevels(res.data ?? []);
    }).catch(() => {
      setComplexityLevels([]);
    });

    if (isEditMode) {
      setSelectedTypeId(String(exercise.type_id || ''));
      setExerciseName(exercise.name || '');
      setTask(exercise.task || '');
      setDescription(exercise.description || '');
      setComplexityLevelId(exercise.complexity_level_id ?? 2);
      setCurrentExerciseId(exercise.id);
      setStep(2);

      // Загружаем существующие варианты
      loadVariants(exercise.id);
    }
  }, [show, exercise?.id]);

  useEffect(() => {
    if (promptMode === 'saved' && selectedPromptId) {
      const found = prompts.find((p) => String(p.id) === selectedPromptId);
      if (found) {
        setPrompt(found.body);
      } else {
        setPromptMode('custom');
        setSelectedPromptId('');
        setPrompt('');
      }
    }
  }, [selectedPromptId, promptMode, prompts]);

  const loadVariants = async (exId) => {
    setLoadingVariants(true);
    try {
      const data = await fetchVariants(exId);
      setVariants(data);
    } catch {
      setVariants([]);
    } finally {
      setLoadingVariants(false);
    }
  };

  const resetAll = () => {
    setStep(1);
    setSelectedTypeId('');
    setShowNewTypeForm(false);
    setNewType({ name: '', description: '', hard_level: 1, parser: 'keyword_transform' });
    setTypeError('');
    setCreatingType(false);
    setExerciseName('');
    setTask('');
    setDescription('');
    setComplexityLevelId(2);
    setHeaderSaving(false);
    setHeaderError('');
    setCurrentExerciseId(null);
    setVariants([]);
    setLoadingVariants(false);
    setDeletingVariantId(null);
    setPromptMode('saved');
    setSelectedPromptId('');
    setPrompt('');
    setPromptOpen(false);
    setSavePromptName('');
    setSavingPrompt(false);
    setSavePromptError('');
    setDeletingPromptId(null);
    setRawText('');
    setParsing(false);
    setParseError('');
    setParsedBlock(null);
    setEditedAnswers({});
    setSavingVariant(false);
    setVariantSaveError('');
    setLastSavedVariantId(null);
    setLastSavedKeyword('');
    setLastSavedQuestion('');
    setShowGrammarRulePrompt(false);
    setShowGrammarRuleModal(false);
    setGrammarRuleVariant(null);
  };

  const handleHide = () => {
    resetAll();
    onHide();
  };

  // ── Step 1 handlers ────────────────────────────────────────────────────────

  const handleCreateType = async () => {
    if (!newType.name.trim()) {
      setTypeError('Введите название типа');
      return;
    }
    setTypeError('');
    setCreatingType(true);
    try {
      const created = await createExerciseType(newType);
      setSelectedTypeId(String(created.id));
      setShowNewTypeForm(false);
    } catch (err) {
      setTypeError(err?.response?.data?.detail ?? 'Ошибка при создании типа');
    } finally {
      setCreatingType(false);
    }
  };

  // ── Step 2 handlers ────────────────────────────────────────────────────────

  const handleSaveHeader = async () => {
    if (!exerciseName.trim()) {
      setHeaderError('Введите название упражнения');
      return;
    }
    setHeaderError('');
    setHeaderSaving(true);
    try {
      const payload = {
        name: exerciseName.trim(),
        task: task.trim() || null,
        description: description.trim() || null,
        type_id: Number(selectedTypeId),
        complexity_level_id: Number(complexityLevelId),
      };
      if (isEditMode && currentExerciseId) {
        await updateExercise(currentExerciseId, payload);
        // В режиме редактирования переходим к шагу 3
        setStep(3);
      } else {
        const created = await createExercise(payload);
        setCurrentExerciseId(created.id);
        setStep(3);
      }
    } catch (err) {
      setHeaderError(err?.response?.data?.detail ?? 'Ошибка при сохранении');
    } finally {
      setHeaderSaving(false);
    }
  };

  // ── Step 3 — Prompt handlers ───────────────────────────────────────────────

  const handleSavePrompt = async () => {
    if (!savePromptName.trim()) { setSavePromptError('Введите название промпта'); return; }
    if (!prompt.trim()) { setSavePromptError('Промпт не может быть пустым'); return; }
    setSavePromptError('');
    setSavingPrompt(true);
    try {
      const created = await createPrompt({ name: savePromptName.trim(), body: prompt });
      setSelectedPromptId(String(created.id));
      setPromptMode('saved');
      setSavePromptName('');
    } catch (err) {
      setSavePromptError(err?.response?.data?.detail ?? 'Ошибка при сохранении промпта');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleDeletePrompt = async (id) => {
    setDeletingPromptId(id);
    try {
      await deletePrompt(id);
      if (String(id) === selectedPromptId) {
        setPromptMode('custom');
        setSelectedPromptId('');
        setPrompt('');
      }
    } finally {
      setDeletingPromptId(null);
    }
  };

  // ── Step 3 — Parse handlers ────────────────────────────────────────────────

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setRawText(ev.target.result);
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  const handleParseText = async () => {
    if (!rawText.trim()) {
      setParseError('Введите текст упражнения');
      return;
    }
    setParseError('');
    setParsedBlock(null);
    setParsing(true);
    try {
      // Передаём exercise_id чтобы бэкенд мог сохранить вариант
      const block = await parseText(rawText, Number(selectedTypeId), currentExerciseId, prompt);
      setParsedBlock(block);
      const initialAnswers = {};
      (block.items ?? []).forEach((item) => {
        initialAnswers[item.number] = item.answer;
      });
      setEditedAnswers(initialAnswers);
    } catch (err) {
      setParseError(err?.response?.data?.detail ?? 'Ошибка при разборе текста');
    } finally {
      setParsing(false);
    }
  };

  const handleAnswerChange = (number, value) => {
    setEditedAnswers((prev) => {
      if (prev[number] === value) return prev;
      return { ...prev, [number]: value };
    });
  };

  const handleAddVariant = async () => {
    if (!parsedBlock) {
      setVariantSaveError('Сначала разберите текст');
      return;
    }
    setVariantSaveError('');
    setSavingVariant(true);
    try {
      // Сохраняем данные для GrammarRuleModal ДО очистки parsedBlock
      const savedKeyword = (parsedBlock.items ?? [])[0]?.keyword ?? '';
      const savedQuestion = (parsedBlock.items ?? [])[0]?.sentence ?? '';

      let savedVariantId = null;

      // Если parse-text уже сохранил вариант (вернул variant_id), перезагружаем список
      // Иначе создаём вариант вручную в новом формате sentences
      if (parsedBlock.variant_id) {
        // Вариант уже создан на бэкенде parse-text
        savedVariantId = parsedBlock.variant_id;
        await loadVariants(currentExerciseId);
      } else {
        // Создаём вариант вручную из parsedBlock в новом формате
        const sentencesDict = {};
        (parsedBlock.items ?? []).forEach((item) => {
          const blankPos = '1';
          sentencesDict[String(item.number)] = {
            sentence: item.sentence,
            words: { [blankPos]: item.keyword ? [item.keyword] : [] },
            answers: { [blankPos]: editedAnswers[item.number] ?? item.answer ?? '' },
            explanation: {},
          };
        });
        const variantPayload = {
          variant: null,
          sentences: sentencesDict,
        };
        const created = await createVariant(currentExerciseId, variantPayload);
        savedVariantId = created?.id ?? null;
        await loadVariants(currentExerciseId);
      }

      // Сброс формы для следующего варианта
      setRawText('');
      setParsedBlock(null);
      setEditedAnswers({});
      setParseError('');

      // Предлагаем привязать грамматическое правило
      if (savedVariantId) {
        setLastSavedVariantId(savedVariantId);
        setLastSavedKeyword(savedKeyword);
        setLastSavedQuestion(savedQuestion);
        setShowGrammarRulePrompt(true);
      }
    } catch (err) {
      setVariantSaveError(err?.response?.data?.detail ?? 'Ошибка при добавлении варианта');
    } finally {
      setSavingVariant(false);
    }
  };

  const handleDeleteVariant = async (variantId) => {
    if (!window.confirm('Удалить этот вариант?')) return;
    setDeletingVariantId(variantId);
    try {
      await deleteVariant(currentExerciseId, variantId);
      setVariants((prev) => prev.filter((v) => v.id !== variantId));
    } catch (err) {
      alert(err?.response?.data?.detail ?? 'Ошибка при удалении варианта');
    } finally {
      setDeletingVariantId(null);
    }
  };

  // ── Modal title ────────────────────────────────────────────────────────────

  const modalTitle = isEditMode
    ? 'Редактирование упражнения'
    : step === 1
    ? 'Создание упражнения — Шаг 1'
    : step === 2
    ? 'Создание упражнения — Шаг 2'
    : 'Создание упражнения — Шаг 3';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
    <Modal show={show} onHide={handleHide} size="xl" backdrop="static">
      <Modal.Header closeButton style={{ padding: '1.25rem 1.5rem' }}>
        <div className="w-100">
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.75rem' }}>
            {modalTitle}
          </Modal.Title>
          <StepIndicator step={step} isEditMode={isEditMode} />
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: '1.5rem' }}>

        {/* ════ Step 1: Тип упражнения ════════════════════════════════════════ */}
        {step === 1 && (
          <>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold mb-3" style={{ fontSize: '0.9375rem' }}>
                Выберите тип упражнения
              </Form.Label>
              {types.length === 0 && (
                <p className="text-muted small mb-3">Типов ещё нет. Создайте новый ниже.</p>
              )}
              <div className="d-flex flex-column gap-2">
                {types.map((t) => (
                  <label
                    key={t.id}
                    htmlFor={`type-${t.id}`}
                    className="d-flex align-items-center gap-3 p-3 rounded"
                    style={{
                      border: `2px solid ${selectedTypeId === String(t.id) ? 'var(--bs-primary)' : '#e9ecef'}`,
                      background: selectedTypeId === String(t.id) ? 'rgba(79,110,247,0.05)' : '#fafbfc',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      borderRadius: '0.6rem',
                    }}
                  >
                    <Form.Check
                      type="radio"
                      id={`type-${t.id}`}
                      name="exerciseType"
                      value={String(t.id)}
                      checked={selectedTypeId === String(t.id)}
                      onChange={(e) => setSelectedTypeId(e.target.value)}
                      className="mb-0"
                    />
                    <div className="flex-grow-1">
                      <div className="fw-medium" style={{ fontSize: '0.9375rem', color: '#1e2a4a' }}>
                        {t.name}
                      </div>
                      {t.description && (
                        <div className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '0.15rem' }}>
                          {t.description}
                        </div>
                      )}
                    </div>
                    <Badge
                      bg={LEVEL_VARIANT[t.hard_level] ?? 'secondary'}
                      style={{ fontSize: '0.7rem', flexShrink: 0 }}
                    >
                      {LEVEL_LABEL[t.hard_level] ?? `Ур. ${t.hard_level}`}
                    </Badge>
                  </label>
                ))}
              </div>
            </Form.Group>

            <Button
              variant={showNewTypeForm ? 'outline-secondary' : 'outline-primary'}
              size="sm"
              onClick={() => setShowNewTypeForm((v) => !v)}
              style={{ borderRadius: '0.4rem', fontWeight: 500 }}
            >
              {showNewTypeForm ? '− Отмена' : '+ Создать новый тип'}
            </Button>

            {showNewTypeForm && (
              <div
                className="mt-3 p-4"
                style={{ background: '#f8f9fc', border: '1px solid #e9ecef', borderRadius: '0.75rem' }}
              >
                <h6 className="fw-semibold mb-3" style={{ color: '#1e2a4a' }}>
                  Новый тип упражнения
                </h6>
                {typeError && (
                  <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
                    {typeError}
                  </Alert>
                )}
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Название *</Form.Label>
                      <Form.Control
                        value={newType.name}
                        onChange={(e) => setNewType((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Грамматическое преобразование"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Уровень</Form.Label>
                      <Form.Select
                        value={newType.hard_level}
                        onChange={(e) => setNewType((p) => ({ ...p, hard_level: Number(e.target.value) }))}
                      >
                        {LEVEL_IDS.map((id) => (
                          <option key={id} value={id}>{id} — {LEVEL_LABEL[id]}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Парсер</Form.Label>
                      <Form.Select
                        value={newType.parser}
                        onChange={(e) => setNewType((p) => ({ ...p, parser: e.target.value }))}
                      >
                        {PARSERS.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Тип формы</Form.Label>
                      <Form.Select
                        value={newType.form_type}
                        onChange={(e) => setNewType((p) => ({ ...p, form_type: e.target.value }))}
                      >
                        {FORM_TYPES.map((ft) => (
                          <option key={ft.value} value={ft.value}>{ft.label}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group>
                      <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>Описание / инструкция</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={2}
                        value={newType.description}
                        onChange={(e) => setNewType((p) => ({ ...p, description: e.target.value }))}
                        placeholder="Преобразуйте слово так, чтобы оно грамматически соответствовало содержанию предложения"
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Button
                  className="mt-3"
                  variant="success"
                  size="sm"
                  onClick={handleCreateType}
                  disabled={creatingType}
                  style={{ borderRadius: '0.4rem', fontWeight: 500 }}
                >
                  {creatingType ? <Spinner size="sm" animation="border" /> : 'Создать тип'}
                </Button>
              </div>
            )}
          </>
        )}

        {/* ════ Step 2: Заголовок упражнения ══════════════════════════════════ */}
        {step === 2 && (
          <>
            {isEditMode && (
              <Form.Group className="mb-4">
                <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                  Тип упражнения
                </Form.Label>
                <Form.Select
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                >
                  <option value="">— не выбрано —</option>
                  {types.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name} — {LEVEL_LABEL[t.hard_level] ?? `Ур. ${t.hard_level}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {headerError && (
              <Alert variant="danger" className="py-2 px-3 mb-3" style={{ fontSize: '0.875rem' }}>
                {headerError}
              </Alert>
            )}

            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                    Название упражнения *
                  </Form.Label>
                  <Form.Control
                    value={exerciseName}
                    onChange={(e) => setExerciseName(e.target.value)}
                    placeholder="What Can Computers Do"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-medium d-flex align-items-center gap-2" style={{ fontSize: '0.875rem' }}>
                    Уровень сложности
                    {(() => {
                      const idx = complexityLevels.findIndex((l) => l.id === complexityLevelId);
                      const level = complexityLevels[idx];
                      const variant = idx >= 0 ? (LEVEL_VARIANTS[idx] ?? 'secondary') : 'secondary';
                      return level ? (
                        <Badge bg={variant} style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          {level.name}
                        </Badge>
                      ) : null;
                    })()}
                  </Form.Label>
                  <Form.Select
                    value={complexityLevelId}
                    onChange={(e) => setComplexityLevelId(Number(e.target.value))}
                  >
                    {complexityLevels.map((level) => (
                      <option key={level.id} value={level.id}>{level.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                    Задание
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={task}
                    onChange={(e) => setTask(e.target.value)}
                    placeholder="Прочитайте текст и выполните задание…"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                    Описание
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Необязательное описание упражнения…"
                  />
                </Form.Group>
              </Col>
            </Row>
          </>
        )}

        {/* ════ Step 3: Варианты ══════════════════════════════════════════════ */}
        {step === 3 && (
          <>
            {/* Список добавленных вариантов */}
            {loadingVariants && (
              <div className="d-flex justify-content-center py-3 mb-3">
                <Spinner animation="border" variant="primary" size="sm" />
              </div>
            )}

            {!loadingVariants && variants.length > 0 && (
              <div className="mb-4">
                <div
                  className="fw-semibold mb-2"
                  style={{ fontSize: '0.875rem', color: '#1e2a4a' }}
                >
                  Добавленные варианты{' '}
                  <Badge bg="success" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                    {variants.length}
                  </Badge>
                </div>
                {variants.map((v) => (
                  <VariantRow
                    key={v.id}
                    variant={v}
                    onDelete={() => handleDeleteVariant(v.id)}
                    deleting={deletingVariantId === v.id}
                    onLinkRule={() => {
                      setGrammarRuleVariant(v);
                      setShowGrammarRuleModal(true);
                    }}
                  />
                ))}
              </div>
            )}

            {/* Предложение привязать грамматическое правило */}
            {showGrammarRulePrompt && lastSavedVariantId && (
              <Alert
                variant="info"
                className="mt-3 mb-3 d-flex align-items-center justify-content-between"
                style={{ borderRadius: '0.75rem' }}
              >
                <span style={{ fontSize: '0.875rem' }}>
                  Вариант добавлен. Хотите привязать грамматическое правило-пояснение?
                </span>
                <div className="d-flex gap-2 ms-3 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="primary"
                    onClick={() => setShowGrammarRuleModal(true)}
                    style={{ borderRadius: '0.5rem', fontWeight: 500 }}
                  >
                    Привязать правило
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setShowGrammarRulePrompt(false)}
                    style={{ borderRadius: '0.5rem', fontWeight: 500 }}
                  >
                    Пропустить
                  </Button>
                </div>
              </Alert>
            )}

            {/* Форма добавления нового варианта */}
            <div
              className="p-4"
              style={{
                background: '#f8f9fc',
                border: '1px solid #e9ecef',
                borderRadius: '0.875rem',
              }}
            >
              <h6 className="fw-semibold mb-3" style={{ color: '#1e2a4a' }}>
                {parsedBlock ? 'Результат разбора' : 'Добавить вариант'}
              </h6>

              {!parsedBlock && (
                <>
                  {/* Текст упражнения */}
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-medium" style={{ fontSize: '0.875rem' }}>
                      Текст упражнения
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={rawText}
                      onChange={(e) => setRawText(e.target.value)}
                      placeholder={
                        'Computers and microchips _________ part of our everyday lives. BECOME\n' +
                        'We read magazines which _________ on computers. PRODUCE'
                      }
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem', borderRadius: '0.5rem' }}
                    />
                  </Form.Group>

                  {/* Промпт OpenAI */}
                  <div className="mb-4">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-secondary text-decoration-none"
                      onClick={() => setPromptOpen((v) => !v)}
                      aria-expanded={promptOpen}
                      style={{ fontSize: '0.875rem', fontWeight: 500 }}
                    >
                      <span style={{ marginRight: '0.35rem' }}>{promptOpen ? '▾' : '▸'}</span>
                      Промпт OpenAI
                      {promptMode === 'saved' && selectedPromptId && (
                        <Badge bg="light" text="dark" className="ms-2" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                          сохранённый
                        </Badge>
                      )}
                    </Button>

                    <Collapse in={promptOpen}>
                      <div
                        className="mt-2 p-3"
                        style={{ background: '#f0f1f5', border: '1px solid #e9ecef', borderRadius: '0.75rem' }}
                      >
                        <div className="d-flex gap-3 mb-3">
                          <Form.Check
                            type="radio"
                            id="prompt-mode-saved"
                            name="promptMode"
                            label="Сохранённый промпт"
                            checked={promptMode === 'saved'}
                            onChange={() => {
                              setPromptMode('saved');
                              if (prompts.length > 0 && !selectedPromptId) {
                                setSelectedPromptId(String(prompts[0].id));
                                setPrompt(prompts[0].body);
                              }
                            }}
                          />
                          <Form.Check
                            type="radio"
                            id="prompt-mode-custom"
                            name="promptMode"
                            label="Свой промпт"
                            checked={promptMode === 'custom'}
                            onChange={() => { setPromptMode('custom'); setPrompt(''); }}
                          />
                        </div>

                        {promptMode === 'saved' && (
                          <>
                            {prompts.length === 0 ? (
                              <p className="text-muted small mb-2">
                                Нет сохранённых промптов. Переключитесь на «Свой промпт».
                              </p>
                            ) : (
                              <div className="mb-2">
                                {prompts.map((p) => (
                                  <div key={p.id} className="d-flex align-items-center gap-2 mb-1">
                                    <Form.Check
                                      type="radio"
                                      id={`prompt-${p.id}`}
                                      name="savedPrompt"
                                      label={p.name}
                                      value={String(p.id)}
                                      checked={selectedPromptId === String(p.id)}
                                      onChange={(e) => setSelectedPromptId(e.target.value)}
                                      className="flex-grow-1"
                                    />
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      style={{ padding: '0 6px', lineHeight: '1.4', borderRadius: '0.35rem' }}
                                      disabled={deletingPromptId === p.id}
                                      onClick={() => handleDeletePrompt(p.id)}
                                      aria-label={`Удалить промпт ${p.name}`}
                                    >
                                      {deletingPromptId === p.id ? (
                                        <Spinner size="sm" animation="border" />
                                      ) : '×'}
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {selectedPromptId && (
                              <Form.Control
                                as="textarea"
                                rows={4}
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                style={{ fontFamily: 'monospace', fontSize: '0.8rem', borderRadius: '0.5rem' }}
                              />
                            )}
                            <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                              Текст можно временно изменить для этого запроса.
                            </Form.Text>
                          </>
                        )}

                        {promptMode === 'custom' && (
                          <>
                            <Form.Control
                              as="textarea"
                              rows={5}
                              value={prompt}
                              onChange={(e) => setPrompt(e.target.value)}
                              placeholder="Введите системный промпт для OpenAI…"
                              style={{ fontFamily: 'monospace', fontSize: '0.8rem', borderRadius: '0.5rem' }}
                            />
                            <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                              Системный промпт, отправляемый в OpenAI вместе с пронумерованными предложениями.
                            </Form.Text>
                            <div className="mt-2">
                              {savePromptError && (
                                <Alert variant="danger" className="py-1 px-2 mb-1" style={{ fontSize: '0.8rem' }}>
                                  {savePromptError}
                                </Alert>
                              )}
                              <InputGroup size="sm">
                                <Form.Control
                                  placeholder="Название для сохранения промпта…"
                                  value={savePromptName}
                                  onChange={(e) => setSavePromptName(e.target.value)}
                                  style={{ borderRadius: '0.4rem 0 0 0.4rem' }}
                                />
                                <Button variant="outline-primary" onClick={handleSavePrompt} disabled={savingPrompt}>
                                  {savingPrompt ? <Spinner size="sm" animation="border" /> : 'Сохранить'}
                                </Button>
                              </InputGroup>
                            </div>
                          </>
                        )}
                      </div>
                    </Collapse>
                  </div>

                  {/* Кнопки разбора */}
                  <div className="d-flex gap-2 align-items-center">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      as="label"
                      style={{ cursor: 'pointer', borderRadius: '0.4rem', fontWeight: 500 }}
                    >
                      Загрузить .txt
                      <input type="file" accept=".txt" style={{ display: 'none' }} onChange={handleFileUpload} />
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleParseText}
                      disabled={parsing}
                      style={{ borderRadius: '0.4rem', fontWeight: 500 }}
                    >
                      {parsing ? (
                        <><Spinner size="sm" animation="border" className="me-1" />Разбираем…</>
                      ) : 'Разобрать текст'}
                    </Button>
                  </div>
                </>
              )}

              {parseError && (
                <Alert variant="danger" className="py-2 px-3 mt-3" style={{ fontSize: '0.875rem' }}>
                  {parseError}
                </Alert>
              )}

              {parsedBlock?.ai_error && (
                <Alert variant="warning" className="py-2 px-3 mt-3" style={{ fontSize: '0.875rem' }}>
                  Ответы не сгенерированы: {parsedBlock.ai_error}
                </Alert>
              )}

              {/* Результат разбора */}
              {parsedBlock && (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <Badge bg="success" style={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {(parsedBlock.items ?? []).length} пунктов
                      </Badge>
                    </div>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0 text-secondary text-decoration-none"
                      style={{ fontSize: '0.8rem' }}
                      onClick={() => { setParsedBlock(null); setRawText(''); setEditedAnswers({}); setParseError(''); }}
                    >
                      ← Изменить текст
                    </Button>
                  </div>

                  <Table bordered hover size="sm" responsive className="mb-3">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '3rem' }}>#</th>
                        <th>Предложение</th>
                        <th style={{ width: '12rem' }}>Ключевое слово</th>
                        <th style={{ width: '15rem' }}>Правильный ответ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const groupedItems = [];
                        (parsedBlock.items ?? []).forEach(item => {
                          const last = groupedItems[groupedItems.length - 1];
                          if (last && last.sentence === item.sentence) {
                            last.subItems.push(item);
                          } else {
                            groupedItems.push({ sentence: item.sentence, subItems: [item] });
                          }
                        });
                        return groupedItems.map((group) => (
                          <tr key={group.subItems[0].number}>
                            <td className="text-muted fw-medium">{group.subItems[0].number}</td>
                            <td style={{ fontSize: '0.8125rem', color: '#495057' }}>{group.sentence}</td>
                            <td>
                              {group.subItems.map((si, idx) => (
                                <div key={si.number} style={idx < group.subItems.length - 1 ? { marginBottom: '0.3rem' } : undefined}>
                                  <code
                                    style={{
                                      fontSize: '0.8rem',
                                      background: 'rgba(79,110,247,0.08)',
                                      padding: '0.15rem 0.4rem',
                                      borderRadius: '0.3rem',
                                      color: 'var(--bs-primary)',
                                    }}
                                  >
                                    {si.keyword}
                                  </code>
                                </div>
                              ))}
                            </td>
                            <td>
                              {group.subItems.map((si, idx) => (
                                <div key={si.number} style={idx < group.subItems.length - 1 ? { marginBottom: '0.3rem' } : undefined}>
                                  <Form.Control
                                    size="sm"
                                    value={editedAnswers[si.number] ?? ''}
                                    onChange={(e) => handleAnswerChange(si.number, e.target.value)}
                                    aria-label={`Ответ на пункт ${si.number}`}
                                    style={{ borderRadius: '0.4rem' }}
                                  />
                                </div>
                              ))}
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </Table>

                  {variantSaveError && (
                    <Alert variant="danger" className="py-2 px-3 mb-2" style={{ fontSize: '0.875rem' }}>
                      {variantSaveError}
                    </Alert>
                  )}

                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleAddVariant}
                    disabled={savingVariant}
                    style={{ borderRadius: '0.4rem', fontWeight: 500 }}
                  >
                    {savingVariant ? (
                      <><Spinner size="sm" animation="border" className="me-1" />Сохраняем…</>
                    ) : 'Сохранить вариант'}
                  </Button>
                </>
              )}
            </div>
          </>
        )}
      </Modal.Body>

      {/* ── Footer ── */}
      <Modal.Footer style={{ padding: '1rem 1.5rem' }}>
        {step === 1 && (
          <>
            <Button variant="secondary" onClick={handleHide}>Отмена</Button>
            <Button
              variant="primary"
              onClick={() => setStep(2)}
              disabled={!selectedTypeId}
              style={{ fontWeight: 500 }}
            >
              Далее →
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            {!isEditMode && (
              <Button variant="secondary" onClick={() => setStep(1)}>← Назад</Button>
            )}
            {isEditMode && (
              <Button variant="secondary" onClick={handleHide}>Отмена</Button>
            )}
            <Button
              variant="primary"
              onClick={handleSaveHeader}
              disabled={headerSaving}
              style={{ fontWeight: 500 }}
            >
              {headerSaving ? (
                <><Spinner size="sm" animation="border" className="me-1" />Сохраняем…</>
              ) : isEditMode ? 'Сохранить → Варианты' : 'Создать → Варианты'}
            </Button>
          </>
        )}

        {step === 3 && (
          <>
            <Button variant="secondary" onClick={() => setStep(2)}>← Назад</Button>
            <div className="d-flex align-items-center gap-2">
              {variants.length > 0 && (
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                  {variants.length} {variants.length === 1 ? 'вариант добавлен' : variants.length < 5 ? 'варианта добавлено' : 'вариантов добавлено'}
                </span>
              )}
              <Button
                variant="success"
                onClick={handleHide}
                style={{ fontWeight: 500 }}
              >
                {variants.length > 0 ? 'Готово' : 'Завершить без вариантов'}
              </Button>
            </div>
          </>
        )}
      </Modal.Footer>
    </Modal>

    {/* GrammarRuleModal рендерится ВНЕ основного Modal во избежание z-index конфликтов */}
    {showGrammarRuleModal && currentExerciseId && (() => {
      // Режим редактирования существующего варианта
      if (grammarRuleVariant) {
        // Extract keyword from new sentences format: sentences["1"].words["1"][0]
        const firstSentence = Object.values(grammarRuleVariant.sentences ?? {})[0] ?? {};
        const firstWords = Object.values(firstSentence.words ?? {})[0] ?? [];
        const keyword = Array.isArray(firstWords) ? firstWords[0] ?? '' : '';
        const question = firstSentence.sentence ?? '';
        return (
          <GrammarRuleModal
            show={showGrammarRuleModal}
            onHide={() => { setShowGrammarRuleModal(false); setGrammarRuleVariant(null); }}
            exerciseId={currentExerciseId}
            variantId={grammarRuleVariant.id}
            typeId={Number(selectedTypeId)}
            exerciseTypeName={types.find((t) => t.id === Number(selectedTypeId))?.name ?? ''}
            variantQuestion={question}
            variantKeyword={keyword}
            currentRuleId={grammarRuleVariant.grammar_rule_id ?? null}
            onRuleSaved={(rule) => {
              setShowGrammarRuleModal(false);
              setGrammarRuleVariant(null);
              // Обновить grammar_rule_id в локальном списке вариантов
              setVariants((prev) => prev.map((v) =>
                v.id === grammarRuleVariant.id
                  ? { ...v, grammar_rule_id: rule?.id ?? null, grammar_rule: rule ?? null }
                  : v
              ));
            }}
          />
        );
      }
      // Режим после создания нового варианта
      if (lastSavedVariantId) {
        return (
          <GrammarRuleModal
            show={showGrammarRuleModal}
            onHide={() => setShowGrammarRuleModal(false)}
            exerciseId={currentExerciseId}
            variantId={lastSavedVariantId}
            typeId={Number(selectedTypeId)}
            exerciseTypeName={types.find((t) => t.id === Number(selectedTypeId))?.name ?? ''}
            variantQuestion={lastSavedQuestion}
            variantKeyword={lastSavedKeyword}
            currentRuleId={null}
            onRuleSaved={() => {
              setShowGrammarRuleModal(false);
              setShowGrammarRulePrompt(false);
              loadVariants(currentExerciseId);
            }}
          />
        );
      }
      return null;
    })()}
    </>
  );
}
